import { CloseButton } from '@/components/ui/close-button';
import type { DialogBodyProps, DialogContentProps, DialogRootProps } from '@chakra-ui/react';
import { Dialog, Portal, VStack } from '@chakra-ui/react';
import type { PropsWithChildren } from 'react';

export interface ModalProps extends DialogRootProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  maxWidth?: string;
  isCentered?: boolean;
  bodyProps?: DialogBodyProps;
  dialogContentProps?: DialogContentProps;
  footer?: React.ReactNode;
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  maxWidth,
  isCentered = false,
  bodyProps,
  dialogContentProps,
  footer,
  ...rest
}: PropsWithChildren<ModalProps>) => {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      placement={isCentered ? 'center' : 'top'}
      {...rest}
    >
      <Portal>
        <Dialog.Backdrop bg="gray.200/50" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content maxW={maxWidth} rounded="2xl" {...dialogContentProps}>
            {title && (
              <Dialog.Header>
                <VStack gap={1} align="stretch">
                  <Dialog.Title fontSize="xl">{title}</Dialog.Title>
                  {description && <Dialog.Description>{description}</Dialog.Description>}
                </VStack>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
            )}
            <Dialog.Body {...bodyProps}>{children}</Dialog.Body>
            {footer && <Dialog.Footer>{footer}</Dialog.Footer>}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
